import React, { FC, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { T } from 'src/shared/helpers';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import type { addPricingRulePanel_PricingsListQuery } from './__generated__/addPricingRulePanel_PricingsListQuery.graphql';
import {
    StackedBody,
    StackedFooterButtons,
    FooterButton,
    PanelItem,
    ItemDot,
    ItemName,
    ItemActionSuccess,
    PanelItemSpaced,
    ItemNameAction,
    EmptyState
} from '../common/styled';

export interface AddPricingRulePanelProps {
    productId: string;
    productModelId: number;
    existingPricingIds: string[];
    onBack: () => void;
    onCreateNew: () => void;
    onAssigned: () => void;
}

const AddPricingRulePanelContent: FC<AddPricingRulePanelProps> = ({ productId, productModelId, existingPricingIds, onBack, onCreateNew, onAssigned }) => {
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addPricingRulePanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addPricingRulePanel_PricingsListQuery>(
        graphql`
            query addPricingRulePanel_PricingsListQuery {
                pricings(first: 200) {
                    edges {
                        node {
                            id
                            pricingName
                            pricingID
                        }
                    }
                }
            }
        `,
        {}
    );

    const allPricings = React.useMemo(() => {
        const edges = data?.pricings?.edges || [];
        return edges.map((edge: { node: { id: string; pricingID: number; pricingName: string } }) => ({
            id: edge.node.id,
            name: edge.node.pricingName || `#${edge.node.pricingID}`
        }));
    }, [data]);

    const [assigning, setAssigning] = useState<string | null>(null);

    const [commitAssign] = useMutation(graphql`
        mutation addPricingRulePanel_AssignMutation($pricingId: ID!, $productIds: [Long!]!) {
            assignProductsToPricing(pricingId: $pricingId, productIds: $productIds) {
                result
            }
        }
    `);

    const handleAssign = (pricingId: string) => {
        setAssigning(pricingId);
        commitAssign({
            variables: { pricingId, productIds: [productModelId] },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableRules = allPricings.filter((pricingRule: { id: string }) => !existingPricingIds.includes(pricingRule.id));
    const hasExistingPricing = existingPricingIds.length > 0;
    const disabledTooltip = T._('This product already has a pricing rule assigned. Remove the existing one before assigning a new one.', 'Admin');

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add pricing rule', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableRules.length === 0 && <EmptyState>{T._('All pricing rules are already assigned', 'Admin')}</EmptyState>}
                {availableRules.map((pricingRule: { id: string; name: string }) => (
                    <ZTooltip key={pricingRule.id} title={hasExistingPricing ? disabledTooltip : undefined} position='top' addContainer>
                        <PanelItem
                            $clickable={!hasExistingPricing && assigning === null}
                            $disabled={hasExistingPricing}
                            onClick={hasExistingPricing || assigning !== null ? undefined : () => handleAssign(pricingRule.id)}
                        >
                            <ItemDot $color='#3b82f6' />
                            <ItemName>{pricingRule.name}</ItemName>
                            <ItemActionSuccess>{assigning === pricingRule.id ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                        </PanelItem>
                    </ZTooltip>
                ))}
                <ZTooltip title={hasExistingPricing ? disabledTooltip : undefined} position='top' addContainer>
                    <PanelItemSpaced $clickable={!hasExistingPricing} $disabled={hasExistingPricing} onClick={hasExistingPricing ? undefined : onCreateNew}>
                        <ItemDot $color='#d1d5db' />
                        <ItemNameAction>+ {T._('Create new', 'Admin')}</ItemNameAction>
                    </PanelItemSpaced>
                </ZTooltip>
            </StackedBody>
        </Drawer>
    );
};

export const AddPricingRulePanel: FC<AddPricingRulePanelProps> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add pricing rule', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </Drawer>
            }
        >
            <AddPricingRulePanelContent {...props} />
        </Suspense>
    );
};
